/* eslint-disable max-len */
/**
 * BillingCyclesModal Component
 *
 * A framework-agnostic, reusable modal for displaying and managing billing cycles.
 *
 * @component
 * @example
 * ```tsx
 * <BillingCyclesModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   data={{
 *     selectedLines: contractLines,
 *     customerNumber: "CUST001",
 *     contractNumber: "CONTRACT001"
 *   }}
 * />
 * ```
 *
 * @props
 * - open: boolean - Controls modal visibility
 * - onOpenChange: (open: boolean) => void - Callback when modal state changes
 * - data: BillingCyclesModalData - Data to display in the modal
 *   - selectedLines: ContractLine[] - Array of contract lines with billing cycles
 *   - customerNumber?: string - Optional customer identifier
 *   - contractNumber?: string - Optional contract identifier
 *   - comment?: string - Optional initial comment
 *   - nextBcStartDate?: string - Optional next billing cycle start date
 *   - reasonType?: string - Optional reason type
 *   - reasonName?: string - Optional reason name
 *
 * @performance
 * - Uses React.memo for child components to prevent unnecessary re-renders
 * - Implements lazy loading for tab content
 * - Optimized state management with minimal re-renders
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/evo/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/evo/ui/alert-dialog';
import Spinner from '@/components/common/Spinner/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/evo/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { Label } from '@/components/evo/ui/label';
import { Checkbox } from '@/components/evo/ui/checkbox';
import { BillingCyclesModalData, BillingCycle } from './types';
import { UserRole } from '@/config/roles.config';
import { BillingCyclesHeader } from './BillingCyclesHeader';
import { CycleVisualization } from './CycleVisualization';
import { RawDataGrid } from './RawDataGrid';
import { InvoiceModal } from './CycleActionModals';

interface BillingCyclesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: BillingCyclesModalData;
  onSave?: (formData: {
    nextBcStartDate: string;
    reasonCode: string;
    comment: string;
  }) => void;
  isSaving?: boolean;
  /** Initial loading state - shows full loading overlay when modal first opens */
  isLoading?: boolean;
  /** Refetching state - shows spinners on individual sections while keeping content visible */
  isRefetching?: boolean;
  onDataChanged?: () => void;
  /** Whether there is an active Pause on the contract or selected contract lines */
  hasPauseActive?: boolean;
  userRole?: UserRole;
}

// Cycles per line options - configurable constant
const CYCLES_PER_LINE_OPTIONS = [
  { value: '3', label: '3' },
  { value: '5', label: '5' },
  { value: '10', label: '10' },
  { value: 'all', label: 'All' },
] as const;

const DEFAULT_CYCLES_PER_LINE = '3';

// Default: take cycles from the end (true = first N cycles, false = last N cycles)
const DEFAULT_TAKE_FROM_START = false;

// Zoom configuration - must match CycleVisualization
const ZOOM_MIN = 100;

export function BillingCyclesModal({
  open,
  onOpenChange,
  data,
  onSave,
  isSaving = false,
  isLoading = false,
  isRefetching = false,
  onDataChanged,
  hasPauseActive = false,
  userRole,
}: BillingCyclesModalProps) {
  // State: Cycles per line selection
  const [cyclesPerLine, setCyclesPerLine] = useState<string>(DEFAULT_CYCLES_PER_LINE);

  // State: Take from start (true = first N cycles, false = last N cycles)
  const [takeFromStart, setTakeFromStart] = useState<boolean>(DEFAULT_TAKE_FROM_START);

  // State: Active tab to conditionally show "Show from start" checkbox
  const [activeTab, setActiveTab] = useState<string>('raw-data');

  // State: Lifted from CycleVisualization to preserve state when switching tabs
  const [visualizationHiddenRows, setVisualizationHiddenRows] = useState<Set<number>>(new Set());
  const [visualizationZoomLevel, setVisualizationZoomLevel] = useState<number>(ZOOM_MIN);
  const [visualizationOpenPopovers, setVisualizationOpenPopovers] = useState<Set<string>>(new Set());

  // State: Bookmarked cycles (resets when modal closes)
  const [visualizationBookmarkedCycles, setVisualizationBookmarkedCycles] = useState<Set<string>>(new Set());

  // State: Lifted InvoiceModal state to prevent unmounting on refetch
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [activeInvoiceCycle, setActiveInvoiceCycle] = useState<BillingCycle | null>(null);

  const [showPauseWarningDialog, setShowPauseWarningDialog] = useState(false);
  const pendingFormDataRef = useRef<{
    nextBcStartDate: string;
    reasonCode: string;
    comment: string;
  } | null>(null);

  // Reset visualization state when modal closes or data changes (mutation occurs)
  useEffect(() => {
    if (!open) {
      setVisualizationHiddenRows(new Set());
      setVisualizationZoomLevel(ZOOM_MIN);
      setVisualizationOpenPopovers(new Set());
      setVisualizationBookmarkedCycles(new Set());
      // Also reset invoice modal state when parent modal closes
      setInvoiceModalOpen(false);
      setActiveInvoiceCycle(null);
      // Reset cycles per line, takeFromStart, and activeTab to defaults
      setCyclesPerLine(DEFAULT_CYCLES_PER_LINE);
      setTakeFromStart(DEFAULT_TAKE_FROM_START);
      setActiveTab('raw-data');
    }
  }, [open]);

  // Handler for opening the invoice modal from CycleActionButtons
  const handleInvoiceCycle = useCallback((cycle: BillingCycle) => {
    setActiveInvoiceCycle(cycle);
    setInvoiceModalOpen(true);
  }, []);

  // Memoized handler to prevent unnecessary re-renders
  const handleCyclesPerLineChange = useCallback((value: string) => {
    setCyclesPerLine(value);
  }, []);

  // Handler for checkbox change (Show from start)
  const handleTakeFromStartChange = useCallback((checked: boolean | 'indeterminate') => {
    setTakeFromStart(checked === true);
  }, []);

  // Handler for data changes (mutations) - reset visualization state
  const handleDataChanged = useCallback(() => {
    setVisualizationHiddenRows(new Set());
    setVisualizationZoomLevel(ZOOM_MIN);
    setVisualizationOpenPopovers(new Set());
    setVisualizationBookmarkedCycles(new Set());
    onDataChanged?.();
  }, [onDataChanged]);

  const handleSaveWithPauseCheck = useCallback((formData: {
    nextBcStartDate: string;
    reasonCode: string;
    comment: string;
  }) => {
    if (hasPauseActive) {
      pendingFormDataRef.current = formData;
      setShowPauseWarningDialog(true);
    } else {
      onSave?.(formData);
    }
  }, [hasPauseActive, onSave]);

  const handleConfirmSave = useCallback(() => {
    if (pendingFormDataRef.current) {
      onSave?.(pendingFormDataRef.current);
      pendingFormDataRef.current = null;
    }
    setShowPauseWarningDialog(false);
  }, [onSave]);

  const handleCancelSave = useCallback(() => {
    pendingFormDataRef.current = null;
    setShowPauseWarningDialog(false);
  }, []);

  // Calculate cycles per line value for child components
  const cyclesPerLineValue = cyclesPerLine === 'all' ? undefined : parseInt(cyclesPerLine, 10);

  // Statuses that make a line read-only (no form, no reinvoice)
  const READ_ONLY_STATUSES = ['Cancelled', 'Expired', 'Terminated'];

  // Determine if the modal should be fully read-only (all selected lines are cancelled/expired/terminated, or READER/USER role)
  const isReaderRole = userRole === UserRole.READER;
  const isUserRole = userRole === UserRole.USER;
  const isReadOnly = isReaderRole || isUserRole || (data.selectedLines.length > 0 &&
    data.selectedLines.every(line => READ_ONLY_STATUSES.includes(line.status)));

  // Set of line IDs that are read-only (for per-cycle reinvoice hiding)
  const readOnlyLineIds = useMemo(() => {
    const ids = new Set<string>();
    data.selectedLines.forEach(line => {
      if (READ_ONLY_STATUSES.includes(line.status)) {
        ids.add(line.id);
      }
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.selectedLines]);

  // Detect mixed selection: at least one read-only line AND at least one valid (non-read-only) line
  const hasMixedStatuses = readOnlyLineIds.size > 0 && readOnlyLineIds.size < data.selectedLines.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Update/Review Billing Cycles</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <Spinner />
                <span className="text-sm text-muted-foreground">Loading billing cycles...</span>
              </div>
            </div>
          )}

          {/* Content - only show when not loading */}
          {!isLoading && (
            <>
              {/* Header Section with Form Inputs - hidden when all selected lines are cancelled/expired/terminated */}
              {!isReadOnly && (
                <div className="relative">
                  {isRefetching && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10 rounded-lg">
                      <Spinner />
                    </div>
                  )}
                  {hasMixedStatuses && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <span className="font-semibold">Note:</span> Some of the selected lines have a status of Cancelled, Expired, or Terminated and will not be updated. Only lines with valid statuses will be affected by changes made here.
                    </div>
                  )}
                  <BillingCyclesHeader data={data} onSave={handleSaveWithPauseCheck} isSaving={isSaving} />
                </div>
              )}

          {/* Cycles per Line Dropdown with Show from start checkbox */}
          <div className="flex items-center gap-4 px-1">
            <Label htmlFor="cycles-per-line" className="text-sm font-medium whitespace-nowrap">
              Cycles per Line:
            </Label>
            <Select value={cyclesPerLine} onValueChange={handleCyclesPerLineChange}>
              <SelectTrigger id="cycles-per-line" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CYCLES_PER_LINE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {cyclesPerLine !== 'all' && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-from-start"
                  checked={takeFromStart}
                  onCheckedChange={handleTakeFromStartChange}
                />
                <Label
                  htmlFor="show-from-start"
                  className="text-sm font-medium cursor-pointer"
                >
                  Show From Beginning
                </Label>
              </div>
            )}
          </div>

              {/* Tabbed Content */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="raw-data">Raw Data</TabsTrigger>
                  <TabsTrigger value="visualization">Cycle Visualization</TabsTrigger>
                </TabsList>

            <TabsContent value="raw-data" className="mt-4">
              <div className="relative">
                {isRefetching && (
                  <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10 rounded-lg min-h-[200px]">
                    <Spinner />
                  </div>
                )}
                                                <RawDataGrid
                                                  selectedLines={data.selectedLines}
                                                  cyclesPerLine={cyclesPerLineValue}
                                                  takeFromStart={takeFromStart}
                                                  onDataChanged={handleDataChanged}
                                                  onInvoiceCycle={handleInvoiceCycle}
                                                  userRole={userRole}
                                                />
              </div>
            </TabsContent>

                <TabsContent value="visualization" className="mt-4">
                  <div className="relative">
                    {isRefetching && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10 rounded-lg min-h-[200px]">
                        <Spinner />
                      </div>
                    )}
                    <CycleVisualization
                      selectedLines={data.selectedLines}
                      cyclesPerLine={cyclesPerLineValue}
                      takeFromStart={takeFromStart}
                      hiddenRows={visualizationHiddenRows}
                      onHiddenRowsChange={setVisualizationHiddenRows}
                      zoomLevel={visualizationZoomLevel}
                      onZoomLevelChange={setVisualizationZoomLevel}
                      openPopovers={visualizationOpenPopovers}
                      onOpenPopoversChange={setVisualizationOpenPopovers}
                      onDataChanged={handleDataChanged}
                      onInvoiceCycle={handleInvoiceCycle}
                      userRole={userRole}
                      bookmarkedCycles={visualizationBookmarkedCycles}
                      onBookmarkedCyclesChange={setVisualizationBookmarkedCycles}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </DialogContent>

      {/* Lifted InvoiceModal - rendered at BillingCyclesModal level to prevent unmounting on refetch */}
      {activeInvoiceCycle && (
        <InvoiceModal
          open={invoiceModalOpen}
          onOpenChange={setInvoiceModalOpen}
          cycle={activeInvoiceCycle}
          onDataChanged={handleDataChanged}
        />
      )}

      <AlertDialog open={showPauseWarningDialog} onOpenChange={setShowPauseWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pause Warning</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">Warning:</span> This action will change the billing date.
              A Pause to billing is set. Please review your Pause End Date to ensure accurate billing.
              <br /><br />
              Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelSave}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
